import { type SankeyLink, type SankeyNode, sankey } from 'd3-sankey'

import {
  displayLabel,
  type FlowKind,
  type FlowOfFunds,
  type FlowRole,
  flowKindOf,
  nodesByName,
  presentColumns,
  roleOf,
} from './flowModel'
import { flowNodeOrder } from './flowOrder'
import { columnTotal, nodeTotals } from './flowTotals'

export interface FlowNodeData {
  name: string
  label: string
  role: FlowRole
}

export interface FlowLinkData {
  source: number
  target: number
  value: number
  kind: FlowKind
  count: number
}

export type LaidOutFlowNode = SankeyNode<FlowNodeData, FlowLinkData>
export type LaidOutFlowLink = SankeyLink<FlowNodeData, FlowLinkData>

export type LabelSide = 'left' | 'right'

export interface FlowLabel {
  name: string
  text: string
  amount: number
  x: number
  y: number
  side: LabelSide
}

export interface FlowHeading {
  role: FlowRole
  title: string
  total: number
  x: number
  side: LabelSide
}

export interface FlowLayout {
  nodes: LaidOutFlowNode[]
  links: LaidOutFlowLink[]
  labels: FlowLabel[]
  headings: FlowHeading[]
}

export const LEGEND_BASELINE = 12
export const HEADING_BASELINE = 40
const PLOT_TOP = 72
const PLOT_BOTTOM = 12
const NODE_WIDTH = 10
const NODE_PADDING = 30
const MIN_NODE_HEIGHT = 3
const LABEL_GAP = 8
const CHAR_WIDTH = 6.6
const MAX_LABEL_CHARS = 28
const MAX_MARGIN_SHARE = 0.24
// A label is the name line over the amount line, about 26px tall.
const LABEL_ABOVE = 10
const LABEL_BELOW = 16
const LABEL_SPACING = 2
const MIN_CHART_HEIGHT = 560
const PIXELS_PER_NODE = 44

const EMPTY_LAYOUT: FlowLayout = {
  nodes: [],
  links: [],
  labels: [],
  headings: [],
}

const LEFT_LABELED_ROLES: FlowRole[] = ['payer', 'received', 'from']

// The first half of the chart labels to the left of its bars, the rest to
// the right, so the middle band of transfers stays free of text.
export function labelSide(role: FlowRole): LabelSide {
  return LEFT_LABELED_ROLES.includes(role) ? 'left' : 'right'
}

export function flowChartHeight(flow: FlowOfFunds): number {
  const perColumn = presentColumns(flow).map(
    (column) =>
      flow.nodes.filter((node) => roleOf(node) === column.role).length,
  )
  const tallest = Math.max(0, ...perColumn)
  return Math.max(
    MIN_CHART_HEIGHT,
    tallest * PIXELS_PER_NODE + PLOT_TOP + PLOT_BOTTOM,
  )
}

export function textWidth(characters: number): number {
  return characters * CHAR_WIDTH
}

const ACCOUNT_NUMBER_TAIL = /\.\.\.\S+$/

// An account label that won't fit keeps its number ("...0887"), since
// the number is what tells two accounts apart.
export function truncateLabel(text: string, maxCharacters: number): string {
  if (text.length <= maxCharacters) return text
  const accountNumber = text.match(ACCOUNT_NUMBER_TAIL)?.[0]
  if (accountNumber && accountNumber.length <= maxCharacters) {
    return accountNumber
  }
  return `${text.slice(0, Math.max(1, maxCharacters - 1)).trimEnd()}…`
}

export function layoutFlow(
  flow: FlowOfFunds,
  width: number,
  height: number,
): FlowLayout {
  const columns = presentColumns(flow)
  if (width <= 0 || height <= 0 || columns.length === 0) return EMPTY_LAYOUT

  const totals = nodeTotals(flow)
  const headings = columns.map((column) => ({
    role: column.role,
    title: column.heading,
    total: columnTotal(flow, column.role, totals),
  }))
  const marginFor = (role: FlowRole) => {
    const heading = headings.find((entry) => entry.role === role)
    const headingCharacters = heading?.title.length ?? 0
    const longestLabel = Math.max(
      headingCharacters,
      ...flow.nodes
        .filter((node) => roleOf(node) === role)
        .map((node) => Math.min(displayLabel(node).length, MAX_LABEL_CHARS)),
    )
    return Math.min(
      textWidth(longestLabel) + LABEL_GAP * 2,
      width * MAX_MARGIN_SHARE,
    )
  }
  const left = marginFor(columns[0].role)
  const right = marginFor(columns[columns.length - 1].role)

  const laidOut = runSankey(flow, columns, [
    [left, PLOT_TOP],
    [width - right, height - PLOT_BOTTOM],
  ])
  if (!laidOut) return EMPTY_LAYOUT
  const { nodes, links } = laidOut
  nodes.forEach(keepVisible)

  return {
    nodes,
    links,
    labels: placeLabels(nodes, labelRoom(nodes, left, right), height),
    headings: headings.map((heading) => {
      const anchor = nodes.find((node) => node.role === heading.role)
      const side = labelSide(heading.role)
      return {
        ...heading,
        side,
        x: side === 'left' ? (anchor?.x1 ?? 0) : (anchor?.x0 ?? 0),
      }
    }),
  }
}

function runSankey(
  flow: FlowOfFunds,
  columns: { role: FlowRole }[],
  extent: [[number, number], [number, number]],
) {
  const byName = nodesByName(flow)
  const order = flowNodeOrder(flow)
  const indexByName = new Map(
    flow.nodes.map((node, index) => [node.name, index]),
  )
  const nodes: FlowNodeData[] = flow.nodes.map((node) => ({
    name: node.name,
    label: displayLabel(node),
    role: roleOf(node),
  }))
  const links: FlowLinkData[] = flow.links.flatMap((link) => {
    const source = indexByName.get(link.source)
    const target = indexByName.get(link.target)
    if (source === undefined || target === undefined) return []
    return [
      {
        source,
        target,
        value: link.value,
        kind: flowKindOf(link, byName),
        count: link.count ?? 0,
      },
    ]
  })
  const columnOf = (role: FlowRole) =>
    columns.findIndex((column) => column.role === role)

  try {
    return sankey<FlowNodeData, FlowLinkData>()
      .nodeWidth(NODE_WIDTH)
      .nodePadding(NODE_PADDING)
      .extent(extent)
      .nodeAlign((node) => columnOf(node.role))
      .nodeSort(
        (first, second) =>
          (order.get(first.name) ?? 0) - (order.get(second.name) ?? 0),
      )({ nodes, links })
  } catch {
    return null
  }
}

// A $40 node next to a $2M one would draw at 0px. Mutated in place
// because the links point at these same node objects.
function keepVisible(node: LaidOutFlowNode) {
  const top = node.y0 ?? 0
  const bottom = node.y1 ?? 0
  if (bottom - top >= MIN_NODE_HEIGHT) return
  const middle = (top + bottom) / 2
  node.y0 = middle - MIN_NODE_HEIGHT / 2
  node.y1 = middle + MIN_NODE_HEIGHT / 2
}

// How many characters fit beside each column: the page margin for an
// outer column, the gap to the neighbor column for an inner one.
function labelRoom(
  nodes: LaidOutFlowNode[],
  left: number,
  right: number,
): Map<FlowRole, number> {
  const columnStarts = [...new Set(nodes.map((node) => node.x0 ?? 0))].sort(
    (first, second) => first - second,
  )
  const room = new Map<FlowRole, number>()
  for (const node of nodes) {
    if (room.has(node.role)) continue
    const pixels = spaceBeside(node, columnStarts, left, right)
    room.set(node.role, Math.floor((pixels - LABEL_GAP) / CHAR_WIDTH))
  }
  return room
}

function spaceBeside(
  node: LaidOutFlowNode,
  columnStarts: number[],
  left: number,
  right: number,
): number {
  const position = columnStarts.indexOf(node.x0 ?? 0)
  if (labelSide(node.role) === 'left') {
    if (position === 0) return left
    return (node.x0 ?? 0) - (columnStarts[position - 1] + NODE_WIDTH)
  }
  if (position === columnStarts.length - 1) return right
  return columnStarts[position + 1] - (node.x1 ?? 0)
}

// Biggest nodes claim their label spot first. A label that would overlap
// one already placed is dropped, and the tooltip carries it instead.
function placeLabels(
  nodes: LaidOutFlowNode[],
  room: Map<FlowRole, number>,
  height: number,
): FlowLabel[] {
  const placed: FlowLabel[] = []
  const bySize = [...nodes].sort(
    (first, second) => (second.value ?? 0) - (first.value ?? 0),
  )
  for (const node of bySize) {
    const x = labelX(node)
    const middle = ((node.y0 ?? 0) + (node.y1 ?? 0)) / 2
    const y = Math.min(Math.max(middle, PLOT_TOP), height - LABEL_BELOW)
    const collides = placed.some(
      (label) =>
        label.x === x &&
        Math.abs(label.y - y) < LABEL_ABOVE + LABEL_BELOW + LABEL_SPACING,
    )
    if (collides) continue
    placed.push({
      name: node.name,
      text: truncateLabel(node.label, room.get(node.role) ?? MAX_LABEL_CHARS),
      amount: node.value ?? 0,
      x,
      y,
      side: labelSide(node.role),
    })
  }
  return placed
}

function labelX(node: LaidOutFlowNode): number {
  return labelSide(node.role) === 'left'
    ? (node.x0 ?? 0) - LABEL_GAP
    : (node.x1 ?? 0) + LABEL_GAP
}
